import type { Ticket, Tech } from '../../types/ticket';
import { formatTimeAgo } from '../../utils/dateUtils';
import {
  AppDrawer,
  AppButton,
  AppChip,
  Box,
  Typography,
  Divider,
  Stack,
  Avatar,
  OpenInNew as OpenInNewIcon,
  PersonSearch as ReassignIcon,
  PauseCircleOutline as HoldIcon,
} from '../ui';

export interface TicketDetailDrawerProps {
  /** Whether the drawer is open */
  open: boolean;
  /** The ticket to display */
  ticket: Ticket | null;
  /** The assigned technician (optional) */
  tech?: Tech;
  /** Callback when drawer is closed */
  onClose: () => void;
  /** Optional callback for reassign action - if provided, shows Reassign button */
  onReassign?: (ticketId: string) => void;
  /** Optional callback for hold action - if provided, shows Hold button */
  onHold?: (ticketId: string, reason?: string) => void;
  /** Optional callback to open full view - if provided, shows Open Full View button */
  onOpenFullView?: (ticketId: string) => void;
  /** Optional SLA status calculator - if not provided, calculates internally */
  getSlaStatus?: (ticket: Ticket) => 'ok' | 'at-risk' | 'breached';
}

// Default SLA status calculator
const defaultGetSlaStatus = (ticket: Ticket): 'ok' | 'at-risk' | 'breached' => {
  if (!ticket.slaDeadline) return 'ok';
  const now = new Date();
  const deadline = new Date(ticket.slaDeadline);
  const minutesRemaining = (deadline.getTime() - now.getTime()) / (1000 * 60);

  if (minutesRemaining <= 0) return 'breached';
  if (minutesRemaining <= 60) return 'at-risk';
  return 'ok';
};

// Format SLA time remaining/overdue
const formatSlaTime = (deadline: Date | null, status: 'ok' | 'at-risk' | 'breached'): string => {
  if (!deadline) return '-';

  const now = new Date();
  const diff = new Date(deadline).getTime() - now.getTime();
  const absDiff = Math.abs(diff);
  const minutes = Math.floor(absDiff / (1000 * 60));
  const hours = Math.floor(minutes / 60);
  const mins = minutes % 60;

  if (status === 'breached') {
    if (hours > 0) return `${hours}h ${mins}m overdue`;
    return `${mins}m overdue`;
  }

  if (hours > 0) return `${hours}h ${mins}m remaining`;
  return `${mins}m remaining`;
};

// SLA status colors
const slaColors: Record<'ok' | 'at-risk' | 'breached', { bg: string; text: string; border: string }> = {
  ok: { bg: 'rgba(16, 124, 16, 0.08)', text: '#107c10', border: 'rgba(16, 124, 16, 0.3)' },
  'at-risk': { bg: 'rgba(202, 80, 16, 0.08)', text: '#ca5010', border: 'rgba(202, 80, 16, 0.3)' },
  breached: { bg: 'rgba(209, 52, 56, 0.08)', text: '#d13438', border: 'rgba(209, 52, 56, 0.3)' },
};

/**
 * TicketDetailDrawer - Unified ticket detail side panel
 *
 * Use this component anywhere you need to show ticket details in a drawer.
 * Pass action callbacks (onReassign, onHold) to enable action buttons.
 */
export const TicketDetailDrawer: React.FC<TicketDetailDrawerProps> = ({
  open,
  ticket,
  tech,
  onClose,
  onReassign,
  onHold,
  onOpenFullView,
  getSlaStatus = defaultGetSlaStatus,
}) => {
  if (!ticket) return null;

  const slaStatus = getSlaStatus(ticket);
  const serviceNowUrl = `https://percepta.service-now.com/nav_to.do?uri=incident.do?sysparm_query=number=${ticket.ticketNumber}`;
  const hasActions = Boolean(onReassign || onHold || onOpenFullView);

  const handleReassign = () => {
    onReassign?.(ticket.id);
    onClose();
  };

  const handleHold = () => {
    onHold?.(ticket.id);
    onClose();
  };

  const handleOpenFullView = () => {
    onOpenFullView?.(ticket.id);
    onClose();
  };

  return (
    <AppDrawer
      open={open}
      onClose={onClose}
      title="Ticket Details"
      subtitle={ticket.ticketNumber}
      width={480}
      footer={
        hasActions ? (
          <Stack direction="row" spacing={1.5} sx={{ width: '100%' }}>
            {onOpenFullView && (
              <AppButton
                variant="primary"
                startIcon={<OpenInNewIcon />}
                onClick={handleOpenFullView}
              >
                Open Full View
              </AppButton>
            )}
            {onReassign && (
              <AppButton
                variant="secondary"
                startIcon={<ReassignIcon />}
                onClick={handleReassign}
              >
                Reassign
              </AppButton>
            )}
            {onHold && (
              <AppButton
                variant="secondary"
                startIcon={<HoldIcon />}
                onClick={handleHold}
              >
                Hold
              </AppButton>
            )}
          </Stack>
        ) : undefined
      }
    >
      {/* ServiceNow Link - at top */}
      <Box
        component="a"
        href={serviceNowUrl}
        target="_blank"
        rel="noopener noreferrer"
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1.5,
          p: 1.5,
          mb: 2.5,
          background: (theme) =>
            theme.palette.mode === 'light'
              ? 'linear-gradient(135deg, #1976d2 0%, #1565c0 100%)'
              : 'linear-gradient(135deg, #90caf9 0%, #64b5f6 100%)',
          borderRadius: 1,
          textDecoration: 'none',
          color: (theme) =>
            theme.palette.mode === 'light' ? '#fff' : 'rgba(0, 0, 0, 0.87)',
          transition: 'transform 0.2s ease, box-shadow 0.2s ease',
          '&:hover': {
            transform: 'translateY(-1px)',
            boxShadow: (theme) =>
              theme.palette.mode === 'light'
                ? '0 4px 16px rgba(25, 118, 210, 0.35)'
                : '0 4px 16px rgba(144, 202, 249, 0.35)',
          },
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: 40,
            height: 40,
            bgcolor: 'rgba(255, 255, 255, 0.15)',
            borderRadius: 1,
          }}
        >
          <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
            <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-1 17.93c-3.95-.49-7-3.85-7-7.93 0-.62.08-1.21.21-1.79L9 15v1c0 1.1.9 2 2 2v1.93zm6.9-2.54c-.26-.81-1-1.39-1.9-1.39h-1v-3c0-.55-.45-1-1-1H8v-2h2c.55 0 1-.45 1-1V7h2c1.1 0 2-.9 2-2v-.41c2.93 1.19 5 4.06 5 7.41 0 2.08-.8 3.97-2.1 5.39z" />
          </svg>
        </Box>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography variant="body2" sx={{ fontWeight: 600 }}>
            Open in ServiceNow
          </Typography>
          <Typography
            variant="caption"
            sx={{
              opacity: 0.8,
              display: 'block',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {serviceNowUrl}
          </Typography>
        </Box>
        <OpenInNewIcon sx={{ fontSize: 18, opacity: 0.8, flexShrink: 0 }} />
      </Box>

      {/* Priority and Status */}
      <Stack direction="row" spacing={1} sx={{ mb: 2.5 }}>
        <AppChip chipVariant="priority" priority={ticket.priority} label={ticket.priority} size="small" />
        <AppChip chipVariant="status" label={ticket.status} size="small" />
      </Stack>

      {/* Description */}
      <Box sx={{ mb: 2.5 }}>
        <Typography variant="overline" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
          Description
        </Typography>
        <Typography variant="body1" sx={{ lineHeight: 1.6 }}>
          {ticket.shortDescription}
        </Typography>
      </Box>

      {/* Tags */}
      <Box sx={{ mb: 2.5 }}>
        <Typography variant="overline" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
          Tags
        </Typography>
        <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
          {(ticket.tags ?? []).map((tag) => (
            <AppChip key={tag} chipVariant="category" label={tag} size="small" />
          ))}
        </Stack>
      </Box>

      {/* SLA Status - below tags */}
      {ticket.slaDeadline && (
        <Box sx={{ mb: 2.5 }}>
          <Typography variant="overline" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
            SLA Status
          </Typography>
          <Box
            sx={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 0.75,
              px: 1.5,
              py: 1,
              borderRadius: 1,
              bgcolor: slaColors[slaStatus].bg,
              border: `1px solid ${slaColors[slaStatus].border}`,
              color: slaColors[slaStatus].text,
            }}
          >
            {slaStatus === 'breached' && (
              <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
                <path d="M8 1a7 7 0 100 14A7 7 0 008 1zm0 10.5a.75.75 0 110-1.5.75.75 0 010 1.5zM8.75 8a.75.75 0 01-1.5 0V5a.75.75 0 011.5 0v3z" />
              </svg>
            )}
            {slaStatus === 'at-risk' && (
              <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
                <path d="M8 1a7 7 0 100 14A7 7 0 008 1zM7.25 4.75a.75.75 0 011.5 0v3.5a.75.75 0 01-1.5 0v-3.5zm.75 7.25a1 1 0 100-2 1 1 0 000 2z" />
              </svg>
            )}
            {slaStatus === 'ok' && (
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5">
                <circle cx="8" cy="8" r="6" />
                <path d="M8 4v4l2.5 1.5" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            )}
            <Typography variant="body2" sx={{ fontWeight: 500, fontSize: '0.8125rem' }}>
              {slaStatus === 'breached' ? 'Breached' : slaStatus === 'at-risk' ? 'At Risk' : 'On Track'}
              {' \u2014 '}
              {formatSlaTime(ticket.slaDeadline, slaStatus)}
            </Typography>
          </Box>
        </Box>
      )}

      <Divider sx={{ my: 2 }} />

      {/* Assigned Technician */}
      <Box sx={{ mb: 3 }}>
        <Typography variant="overline" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
          Assigned To
        </Typography>
        {tech ? (
          <Stack direction="row" spacing={1.5} alignItems="center">
            <Avatar
              sx={{
                width: 40,
                height: 40,
                bgcolor: 'primary.main',
                fontSize: '0.875rem',
                fontWeight: 600,
              }}
            >
              {tech.name.split(' ').map((n) => n[0]).join('')}
            </Avatar>
            <Box>
              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                {tech.name}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {tech.tier} \u2022 {tech.location} \u2022 {tech.currentLoad}/{tech.maxLoad} tickets
              </Typography>
            </Box>
          </Stack>
        ) : (
          <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
            Unassigned
          </Typography>
        )}
      </Box>

      {/* Metadata Grid */}
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: 'repeat(2, 1fr)',
          gap: 2,
          mb: 3,
        }}
      >
        <Box>
          <Typography variant="overline" color="text.secondary" sx={{ display: 'block', mb: 0.25 }}>
            Location
          </Typography>
          <Typography variant="body2" sx={{ fontWeight: 500 }}>
            {ticket.location}
          </Typography>
        </Box>
        <Box>
          <Typography variant="overline" color="text.secondary" sx={{ display: 'block', mb: 0.25 }}>
            Created
          </Typography>
          <Typography variant="body2" sx={{ fontWeight: 500 }}>
            {formatTimeAgo(ticket.createdAt)}
          </Typography>
        </Box>
        <Box>
          <Typography variant="overline" color="text.secondary" sx={{ display: 'block', mb: 0.25 }}>
            Last Updated
          </Typography>
          <Typography variant="body2" sx={{ fontWeight: 500 }}>
            {formatTimeAgo(ticket.updatedAt)}
          </Typography>
        </Box>
        {ticket.slaDeadline && (
          <Box>
            <Typography variant="overline" color="text.secondary" sx={{ display: 'block', mb: 0.25 }}>
              SLA Deadline
            </Typography>
            <Typography variant="body2" sx={{ fontWeight: 500 }}>
              {new Date(ticket.slaDeadline).toLocaleString()}
            </Typography>
          </Box>
        )}
      </Box>

      {/* Notes */}
      {ticket.notes && (
        <Box>
          <Typography variant="overline" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
            Notes
          </Typography>
          <Box
            sx={{
              p: 1.5,
              bgcolor: 'action.hover',
              borderRadius: 1,
            }}
          >
            <Typography
              variant="body2"
              sx={{
                fontStyle: 'italic',
                color: 'text.secondary',
                lineHeight: 1.6,
              }}
            >
              {ticket.notes}
            </Typography>
          </Box>
        </Box>
      )}
    </AppDrawer>
  );
};
